import * as Sentry from '@sentry/react';

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/react/
const env = process.env.REACT_APP_ENV;
const isProd = !!env && /production/i.test(env);

// Error patterns to ignore - keep in sync with nextjs/app/lib/sentry/ignore-errors.ts
const ignoreErrors = [
  // Existing filters
  /SecurityError.*Failed to read.*from 'Window'.*Blocked a frame with origin "(?!.*staging)[^"]*"/,
  /An SSL error has occurred and a secure connection to the server cannot be made/,

  // Cross-origin security errors (high volume noise)
  /SecurityError.*Blocked a frame with origin.*from accessing a cross-origin frame/,
  /Blocked a frame with origin.*invoicesimple\.com.*from accessing a cross-origin frame/,
  /Failed to set a named property.*Blocked a frame with origin/,
  /Permission.*denied.*to access property.*on cross-origin object/,

  // Browser extension interference
  /^Non-Error promise rejection captured/,
  /ResizeObserver loop limit exceeded/,
  /Loading chunk \d+ failed/,
  /Loading CSS chunk/,

  // Service Worker registration issues (non-critical)
  /ServiceWorker script.*threw an exception during script evaluation/,
  /ServiceWorker script.*for scope.*threw an exception/,

  // Third-party SDK errors that don't affect core functionality
  /Flagsmith.*failed to fetch/,
  /Failed to fetch.*flagsmith/i,

  // Generic network timeouts and load failures from browsers
  /NetworkError when attempting to fetch resource/,
  /Load failed.*chunks\/framework/,
  /Load failed.*main-app.*\.js/,
  /Failed to fetch.*webpack/,
  /ChunkLoadError/,

  // Browser compatibility and extension issues
  /'get' on proxy.*javaEnabled.*read-only/,
  /'javaEnabled' is a read-only/,

  // Incomplete app router transactions (Next.js noise)
  /incomplete-app-router-transaction/,

  // Browser-specific errors that don't affect functionality
  /AbortError.*The operation was aborted/,
  /The request timed out/,
  /Request was throttled/,

  // Third-party iframe and widget errors
  /.*chrome-extension:\/\/.*/,
  /.*moz-extension:\/\/.*/,
  /.*safari-extension:\/\/.*/,
  /.*app:\/\/.*webkit.*\.js/,

  // Browser extension communication errors
  /Attempting to use a disconnected port object/,
  /extensionServiceWorker\.js/,

  // Acorn Finance widget errors (external service)
  /Failed to fetch.*af-standard/,
  /.*acorn-finance.*postActivity/,

  // Generic fetch failures that are retried automatically
  /fetch.*TypeError.*Failed to fetch/,
  /fetch.*Load failed/,

  // Next.js hydration mismatches (common SSR/SSG issues, often not immediately actionable)
  /Hydration failed because the initial UI does not match/,
  /There was an error while hydrating.*Suspense boundary/,
  /Text content does not match server-rendered HTML/,
  /Hydration text mismatch/,

  // Service Worker registration failures (browser/network specific)
  /Failed to register a ServiceWorker.*script evaluation failed/,
  /ServiceWorker registration failed/,

  // Custom Element Registry conflicts (browser extensions/widgets)
  /Failed to execute 'define' on 'CustomElementRegistry'.*already been used/,
  /CustomElementRegistry.*already defined/,

  // Third-party analytics script failures
  /heap is not defined/,
  /gtag is not defined/,
  /fbq is not defined/,
  /dataLayer is not defined/,
  // Safari/WebKit wording of the same ReferenceErrors - https://invoice-simple.sentry.io/issues/IS-WEB-APP-HM7
  /Can't find variable: (heap|gtag|fbq|dataLayer)$/,

  // reCAPTCHA third-party script errors (Google's minified code)
  /Cannot read properties of undefined.*reading.*recaptcha/i,
  /TypeError.*recaptcha.*\.js/,

  // Third-party scripts parsing JSON-LD structured data - https://invoice-simple.sentry.io/issues/IS-WEB-APP-FSF
  /evaluating 'r\["@context"\]\.toLowerCase'/,
  /Cannot read properties of undefined.*reading '@context'/,

  // Generic network/fetch errors without specific API context
  /^Failed to fetch$/,
  /^Load failed$/,
  /^Network Error$/,
  /NetworkError.*fetching a resource/,

  // Analytics event tracking network failures (non-critical)
  /Network Error.*api\/v2\/event-all/,
  /trackApiEvent.*Network Error/,

  // PayPal network connectivity errors (client-side, non-actionable)
  /PayPal network error/,
  /PayPal request timeout/,
  /PayPal network connectivity issue/,

  // PDF viewer and browser plugin errors
  /PDF.*viewer.*error/,
  /Adobe.*Acrobat.*error/
];

Sentry.init({
  dsn: 'https://2880a5c84220f6674f5cceaa359706dd@o4507426604449792.ingest.us.sentry.io/4508619537514496',

  // Add optional integrations for additional features
  integrations: [Sentry.replayIntegration()],

  // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
  tracesSampleRate: isProd ? 0.5 : 1.0,

  // Define how likely Replay events are sampled.
  // Only track replays on errors
  replaysSessionSampleRate: 0,

  // Define how likely Replay events are sampled when an error occurs.
  replaysOnErrorSampleRate: isProd ? 0.25 : 0.5,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,
  environment: env,
  release: process.env.PORTER_IMAGE_TAG || 'web-app@1.0.0',
  sendDefaultPii: true,
  ignoreErrors
});
